import { parseMoneyAmount } from "@/shared/lib/money";

import type {
  CustomerPII,
  Lifecycle,
  Marketplace,
  Money,
  Order,
  OrderItem,
  Payment,
  Settlement,
} from "./types";

/** Wire money is a decimal-baht string ("27.38"); MSW view-model fixtures may still pass numbers (already baht). */
export type RawAmount = string | number;

export interface RawMoney {
  original: RawAmount;
  item_discount?: RawAmount; // absent only on MSW fixtures predating this field
  discount: RawAmount;
  shipping: RawAmount;
  order_fee: RawAmount;
  total: RawAmount;
}

export interface RawSettlement {
  buyer_paid: RawAmount;
  marketplace_fees: RawAmount;
  net_payout: RawAmount;
  settled_at: string | null;
  invariant_ok: boolean;
  has_fee_breakdown: boolean;
}

/** Boundary parse: string → parseMoneyAmount; number passes through; undefined → 0. */
export function toAmount(v: RawAmount | undefined): number {
  if (typeof v === "string") return parseMoneyAmount(v);
  return v ?? 0;
}

export function toMoney(m: RawMoney): Money {
  return {
    original: toAmount(m.original),
    item_discount: toAmount(m.item_discount),
    discount: toAmount(m.discount),
    shipping: toAmount(m.shipping),
    order_fee: toAmount(m.order_fee),
    total: toAmount(m.total),
  };
}

export function toSettlement(s: RawSettlement | null | undefined): Settlement | null {
  if (!s) return null;
  return {
    buyer_paid: toAmount(s.buyer_paid),
    marketplace_fees: toAmount(s.marketplace_fees),
    net_payout: toAmount(s.net_payout),
    settled_at: s.settled_at ?? null,
    invariant_ok: Boolean(s.invariant_ok),
    has_fee_breakdown: Boolean(s.has_fee_breakdown),
  };
}

/**
 * A raw item from either the BE snake_case shape or the MSW view-model shape.
 * All fields are optional so the bridge accepts both without crashing.
 */
export interface RawOrderItem {
  // view-model (MSW) field names
  sino?: string;
  merch?: string;
  name?: string;
  qty?: number;
  unit?: RawAmount;
  gift?: boolean;
  bundle?: boolean;
  unmapped?: boolean;
  imageUrl?: string;
  // BE snake_case field names (real backend detail response)
  sino_sku?: string;
  merchant_sku?: string;
  product_name?: string;
  unit_price?: RawAmount;
  is_freebie?: boolean;
  is_bundle?: boolean;
  is_unmapped?: boolean;
  image_url?: string | null;
  original_price?: RawAmount;
  originalPrice?: RawAmount;
  discount?: RawAmount;
  total?: RawAmount;
}

/**
 * A raw orders-list row: either the BE thin projection (GET /v1/orders) or the
 * full MSW view-model. Every field is optional because the real backend omits
 * most of them.
 */
export interface RawOrderListItem {
  // view-model fields (present when MSW serves the full shape)
  id?: string;
  sale?: string;
  mkp?: Marketplace;
  seller?: string;
  created?: string;
  cust?: string;
  phone?: string;
  status?: Lifecycle;
  pay?: Payment;
  tracking?: string | null;
  exc?: boolean;
  excReason?: string;
  attempts?: number;
  splitIndex?: number;
  splitTotal?: number;
  items?: RawOrderItem[];
  money?: RawMoney;
  /** Seller-side settlement record (BE detail endpoint); absent/null until settlement exists. */
  settlement?: RawSettlement | null;
  // BE thin-projection field names
  sale_order_id?: string;
  marketplace?: string;
  seller_id?: string;
  lifecycle_status?: string;
  marketplace_status?: string;
  // view-model passthrough (MSW fixtures may serve the vm field directly)
  marketplaceStatus?: string;
  push_failed?: boolean;
  exc_reason?: string;
  // BE enriched projection field names
  seller_name?: string;
  payment_method?: Payment;
  payment_channel?: string;
  is_cod?: boolean;
  total_amount?: RawAmount;
  settled?: boolean;
  currency?: string;
  placed_at?: string | null;
  item_count?: number;
  created_at?: string;
  // BE detail-only fields (snake_case)
  courier?: string;
  warehouse?: string;
  tracking_no?: string | null;
  platform_shop_name?: string;
  /** True when a shipping label/AWB has been generated. */
  has_awb?: boolean;
  /** True when this order carries a GWP gift line. */
  has_gift?: boolean;
  /** True when fulfilled by the seller's own fleet rather than a marketplace courier. */
  is_seller_own_fleet?: boolean;
  /** Deadline the order must ship by; null/absent = no deadline given. */
  ship_by?: string | null;
  /** Customer display name (BE thin-projection field; `cust` is the vm passthrough). */
  customer_name?: string;
  /** Customer phone, already masked by the BE (BE thin-projection field; `phone` is the vm passthrough). */
  customer_phone?: string;
  /** Typed manual-review causes (BE `review_reasons`); absent when empty or not yet returned. */
  review_reasons?: (
    | "sku_unmapped"
    | "money_parse_failed"
    | "non_thb_currency"
    | "oversold"
    | "buyer_cancel_requested"
  )[];
  /** Per-reason structured context (BE `review_meta`); absent for a healthy order. */
  review_meta?: Record<string, Record<string, unknown>>;
  /** True when the order has an open order_holds row (BE `hold`). */
  hold?: boolean;
  /** Reason applied to a cancel (BE `cancel_reason`); absent when never cancelled. */
  cancel_reason?: string;
  /** Actor who cancelled the order (BE `cancelled_by`); absent when never cancelled. */
  cancelled_by?: string;
  /** ISO 8601 timestamp the order was cancelled (BE `cancelled_at`); null/absent when not cancelled. */
  cancelled_at?: string | null;
  paid_at?: string | null;
  shipped_at?: string | null;
  completed_at?: string | null;
  /** Sibling orders.id values sharing this order's sale_order_id, excluding itself (BE detail-only). */
  related_order_ids?: string[];
  // BE customer PII sub-object (detail endpoint only — absent on list projection)
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
    social?: string;
    billing_name?: string;
    shipping_address?: string;
    billing_address?: string;
  };
  // view-model passthrough (MSW fixtures may serve the camelCase vm fields directly)
  platformShopName?: string;
  paidAt?: string | null;
  shippedAt?: string | null;
  completedAt?: string | null;
}

const ZERO_MONEY: Money = {
  original: 0,
  item_discount: 0,
  discount: 0,
  shipping: 0,
  order_fee: 0,
  total: 0,
};

/**
 * Bridge a raw item (either BE snake_case or MSW view-model shape) to the
 * canonical OrderItem view-model. Prefers the view-model field when both exist.
 */
function toItem(it: RawOrderItem): OrderItem {
  // Resolve imageUrl: prefer VM field; null collapses to undefined so the VM stays clean.
  const imageUrl = it.imageUrl ?? it.image_url ?? undefined;
  return {
    sino: it.sino ?? it.sino_sku ?? "",
    merch: it.merch ?? it.merchant_sku ?? "",
    name: it.name ?? it.product_name ?? "",
    qty: it.qty ?? 0,
    unit: toAmount(it.unit ?? it.unit_price),
    gift: it.gift ?? it.is_freebie ?? false,
    bundle: it.bundle ?? it.is_bundle ?? false,
    unmapped: it.unmapped ?? it.is_unmapped ?? false,
    // exactOptionalPropertyTypes: only include when defined.
    ...(imageUrl !== undefined && { imageUrl }),
    ...((it.originalPrice ?? it.original_price) !== undefined && {
      originalPrice: toAmount(it.originalPrice ?? it.original_price),
    }),
    ...(it.discount !== undefined && { discount: toAmount(it.discount) }),
    ...(it.total !== undefined && { total: toAmount(it.total) }),
  };
}

/**
 * Normalise an orders-list row into the `Order` view-model.
 *
 * The real backend returns a thinner projection than the table renders: it has
 * no `money`/`items`, and uses BE field names (`marketplace`, `lifecycle_status`)
 * instead of the view-model's (`mkp`, `status`). MSW serves the already-full
 * view-model. This mapper bridges both — it maps BE names when the view-model
 * field is absent and defaults the objects the table dereferences (`money`,
 * `items`) so the Orders page never crashes on a thin response.
 */
export function toOrder(r: RawOrderListItem): Order {
  const excReason = r.excReason ?? r.exc_reason;
  return {
    id: r.id ?? "",
    sale: r.sale ?? r.sale_order_id ?? r.id ?? "",
    mkp: r.mkp ?? (r.marketplace as Marketplace | undefined) ?? "shopee",
    sellerId: r.seller_id ?? "",
    seller: r.seller_name ?? r.seller ?? r.seller_id ?? "",
    created: r.placed_at ?? r.created ?? r.created_at ?? "",
    cust: r.cust ?? r.customer_name ?? "",
    phone: r.phone ?? r.customer_phone ?? "",
    status: r.status ?? (r.lifecycle_status as Lifecycle | undefined) ?? "received",
    pay: r.payment_method ?? r.pay ?? "prepaid",
    tracking: r.tracking ?? r.tracking_no ?? null,
    exc: r.exc ?? r.push_failed ?? false,
    items: (r.items ?? []).map(toItem),
    money: r.money ? toMoney(r.money) : { ...ZERO_MONEY, total: toAmount(r.total_amount) },
    settlement: toSettlement(r.settlement),
    settled: r.settled ?? false,
    itemCount: r.item_count ?? r.items?.length ?? 0,
    // exactOptionalPropertyTypes: only include optional fields when defined.
    ...((r.marketplace_status ?? r.marketplaceStatus) !== undefined && {
      marketplaceStatus: r.marketplace_status ?? r.marketplaceStatus,
    }),
    ...(excReason !== undefined && { excReason }),
    ...(r.attempts !== undefined && { attempts: r.attempts }),
    ...(r.splitIndex !== undefined && { splitIndex: r.splitIndex }),
    ...(r.splitTotal !== undefined && { splitTotal: r.splitTotal }),
    ...(r.currency !== undefined && r.currency !== "" && { currency: r.currency }),
    ...(r.payment_channel !== undefined &&
      r.payment_channel !== "" && {
        payChannel: r.payment_channel,
      }),
    ...(r.courier !== undefined && { courier: r.courier }),
    ...(r.warehouse !== undefined && { warehouse: r.warehouse }),
    ...(r.has_awb !== undefined && { hasAwb: r.has_awb }),
    ...(r.has_gift !== undefined && { hasGift: r.has_gift }),
    ...(r.is_seller_own_fleet !== undefined && { isSellerOwnFleet: r.is_seller_own_fleet }),
    ...(r.ship_by !== undefined && { shipBy: r.ship_by }),
    ...((r.platform_shop_name ?? r.platformShopName) !== undefined && {
      platformShopName: r.platform_shop_name ?? r.platformShopName,
    }),
    ...((r.paid_at ?? r.paidAt) !== undefined && { paidAt: r.paid_at ?? r.paidAt }),
    ...((r.shipped_at ?? r.shippedAt) !== undefined && { shippedAt: r.shipped_at ?? r.shippedAt }),
    ...((r.completed_at ?? r.completedAt) !== undefined && {
      completedAt: r.completed_at ?? r.completedAt,
    }),
    ...(r.review_reasons !== undefined &&
      r.review_reasons.length > 0 && { reviewReasons: r.review_reasons }),
    ...(r.review_meta !== undefined && { reviewMeta: r.review_meta }),
    ...(r.hold !== undefined && { hold: r.hold }),
    ...(r.cancel_reason !== undefined && { cancelReason: r.cancel_reason }),
    ...(r.cancelled_by !== undefined && { cancelledBy: r.cancelled_by }),
    ...(r.cancelled_at !== undefined && { cancelledAt: r.cancelled_at }),
    ...(r.related_order_ids !== undefined &&
      r.related_order_ids.length > 0 && { relatedOrderIds: r.related_order_ids }),
    // BE detail endpoint returns a customer sub-object; absent on the list projection.
    // Map it to the canonical CustomerPII view-model only when present.
    ...(r.customer !== undefined && {
      customer: {
        name: r.customer.name ?? "",
        phone: r.customer.phone ?? "",
        email: r.customer.email ?? "",
        social: r.customer.social ?? "",
        billing_name: r.customer.billing_name ?? "",
        shipping_address: r.customer.shipping_address ?? "",
        billing_address: r.customer.billing_address ?? "",
      } satisfies CustomerPII,
    }),
  };
}
